import React, { useState, useEffect } from 'react';
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { GOOGLE_CLIENT_ID, ALLOWED_DOMAIN } from '../config/google';

interface OAuthLoginProps {
  onSuccess: (userEmail: string) => void;
}

const OAuthLogin: React.FC<OAuthLoginProps> = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Check for OAuth parameters in URL and auto-login if coming from SSO hub
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const clientId = urlParams.get('client_id');
    
    // Debug: Log Google Client ID
    console.log('Google Client ID from env:', GOOGLE_CLIENT_ID);
    
    if (clientId) {
      // We're in OAuth authorization flow
      console.log('OAuth authorization flow detected for client:', clientId);
      
      // Check if user is coming from SSO hub with auto-login
      const ssoAutoLogin = sessionStorage.getItem('sso_auto_login');
      if (ssoAutoLogin) {
        console.log('Auto-login detected from SSO hub for user:', ssoAutoLogin);
        
        // Clear the auto-login flag
        sessionStorage.removeItem('sso_auto_login');
        
        // Auto-submit the login form with the user's credentials
        setEmail(ssoAutoLogin);
        
        // Trigger login automatically after a brief delay
        setTimeout(() => {
          handleAutoSubmit(ssoAutoLogin, clientId, urlParams);
        }, 100);
      }
    }
  }, []);

  const handleAutoSubmit = async (userEmail: string, clientId: string, urlParams: URLSearchParams) => {
    setLoading(true);
    setError('');

    try {
      const redirectUri = urlParams.get('redirect_uri');
      const scope = urlParams.get('scope');
      const state = urlParams.get('state');

      // Use the stored user session to auto-authenticate
      const response = await fetch('/api/oauth/authorize', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          client_id: clientId,
          redirect_uri: redirectUri,
          scope: scope || 'openid profile email',
          state: state || '',
          email: userEmail,
          auto_authorize: true
        })
      });

      const data = await response.json();

      if (response.ok) {
        // Redirect to the client application
        window.location.href = data.redirect_url;
      } else {
        setError(data.error_description || 'Auto-authentication failed');
        setLoading(false);
      }
    } catch (error) {
      setError('Network error during auto-authentication. Please try manual login.');
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    setLoading(true);
    setError('');

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const clientId = urlParams.get('client_id');
      const redirectUri = urlParams.get('redirect_uri');
      const scope = urlParams.get('scope');
      const state = urlParams.get('state');

      if (clientId && redirectUri) {
        // OAuth authorization flow with Google token
        const response = await fetch('/api/oauth/authorize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: clientId,
            redirect_uri: redirectUri,
            scope: scope || 'openid profile email',
            state: state || '',
            google_token: credentialResponse.credential
          })
        });

        const data = await response.json();

        if (response.ok) {
          window.location.href = data.redirect_url;
        } else {
          setError(data.error_description || 'Authentication failed');
        }
      } else {
        // Regular SSO Hub login with Google - use authorize endpoint with dummy OAuth params
        const response = await fetch('/api/oauth/authorize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: 'sso-hub',  // Dummy client for direct SSO login
            redirect_uri: window.location.origin,
            scope: 'openid profile email',
            state: '',
            google_token: credentialResponse.credential
          })
        });

        const data = await response.json();

        if (response.ok) {
          // Extract user email from the response and login directly
          // For direct SSO login, we just need to get the user info
          const authCode = new URL(data.redirect_url).searchParams.get('code');
          if (authCode) {
            // We have an auth code, now we can extract user info
            // For simplicity, we'll decode the Google token directly
            try {
              const payload = JSON.parse(atob(credentialResponse.credential.split('.')[1]));
              onSuccess(payload.email);
            } catch (e) {
              setError('Failed to decode user information');
            }
          }
        } else {
          setError(data.error_description || 'Google authentication failed');
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Google login failed. Please try again.');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const urlParams = new URLSearchParams(window.location.search);
      const clientId = urlParams.get('client_id');
      const redirectUri = urlParams.get('redirect_uri');
      const scope = urlParams.get('scope');
      const state = urlParams.get('state');

      if (clientId && redirectUri) {
        // OAuth authorization flow
        const response = await fetch('/api/oauth/authorize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            client_id: clientId,
            redirect_uri: redirectUri,
            scope: scope || 'openid profile email',
            state: state || '',
            email,
            password
          })
        });

        const data = await response.json();

        if (response.ok) {
          // Redirect to the client application
          window.location.href = data.redirect_url;
        } else {
          setError(data.error_description || 'Authentication failed');
        }
      } else {
        // Regular SSO Hub login
        const users = [
          { email: 'rajesh@mactai.com', password: 'rajesh123', name: 'Rajesh' },
          { email: 'madhava@mactai.com', password: 'madhava123', name: 'Madhava' }
        ];

        const user = users.find(u => u.email === email && u.password === password);
        
        if (user) {
          onSuccess(user.email);
        } else {
          setError('Invalid email or password');
        }
      }
    } catch (error) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const urlParams = new URLSearchParams(window.location.search);
  const clientId = urlParams.get('client_id');
  const isOAuthFlow = !!clientId;

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#f5f5f5'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '2rem',
        borderRadius: '8px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: '400px'
      }}>
        <h1 style={{
          textAlign: 'center',
          marginBottom: '1rem',
          color: '#333',
          fontSize: '2rem'
        }}>
          MACT SSO Hub
        </h1>

        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        {/* Debug: Check if client ID is loaded */}
        {!GOOGLE_CLIENT_ID && (
          <div style={{ color: 'red', marginBottom: '1rem' }}>
            Error: Google Client ID not configured
          </div>
        )}

        {isOAuthFlow && (
          <div style={{
            backgroundColor: '#e7f3ff',
            padding: '1rem',
            borderRadius: '4px',
            marginBottom: '1.5rem',
            border: '1px solid #b8daff'
          }}>
            <p style={{
              margin: 0,
              color: '#004085',
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              Authorizing access for: <strong>{clientId.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</strong>
            </p>
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: '#555'
            }}>
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>
          
          <div style={{ marginBottom: '1rem' }}>
            <label style={{
              display: 'block',
              marginBottom: '0.5rem',
              fontWeight: 'bold',
              color: '#555'
            }}>
              Password
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: '1px solid #ddd',
                borderRadius: '4px',
                fontSize: '1rem'
              }}
            />
          </div>
          
          {error && (
            <div style={{
              color: 'red',
              marginBottom: '1rem',
              textAlign: 'center',
              padding: '0.5rem',
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '4px'
            }}>
              {error}
            </div>
          )}
          
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.75rem',
              backgroundColor: loading ? '#6c757d' : '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '1rem',
              cursor: loading ? 'not-allowed' : 'pointer'
            }}
          >
            {loading ? 'Authenticating...' : (isOAuthFlow ? 'Authorize' : 'Login')}
          </button>
        </form>

        <div style={{
          marginTop: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{
            position: 'relative',
            margin: '1rem 0'
          }}>
            <hr style={{
              border: 'none',
              borderTop: '1px solid #ddd',
              margin: '1.5rem 0'
            }} />
            <span style={{
              position: 'absolute',
              top: '-12px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'white',
              padding: '0 1rem',
              color: '#666'
            }}>
              OR
            </span>
          </div>
          
          <GoogleLogin
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            text="signin_with"
            shape="rectangular"
            theme="outline"
            size="large"
            useOneTap={false}
            auto_select={false}
          />
          
          <p style={{
            marginTop: '0.5rem',
            fontSize: '0.85rem',
            color: '#666'
          }}>
            Only @{ALLOWED_DOMAIN} accounts are allowed
          </p>
        </div>
        </GoogleOAuthProvider>
      </div>
    </div>
  );
};

export default OAuthLogin;